import { SetStateAction } from 'react'

import {
    FocusHighlight,
    QuestionItem,
    TableOfContentsEntry,
    ValidationQuestion,
    ValidationQuestionDefinition,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/types'
import { createStandardChecks } from 'ComponentLibrary/src/playground/tools/PdfValidator/utils/pdf-validator-helpers'

export type PdfValidatorState = {
    uploadedFile: File | null
    pdfUrl: string | null
    pageTexts: string[]
    uploadError: string | null
    checksError: string | null
    questionsError: string | null
    tableOfContentsError: string | null
    isPreprocessing: boolean
    isTableOfContentsLoading: boolean
    isChecksRunning: boolean
    isQuestionsRunning: boolean
    focusHighlight: FocusHighlight
    hasLoadedTableOfContents: boolean
    tableOfContentsEntries: TableOfContentsEntry[]
    standardChecks: ValidationQuestion[]
    questions: ValidationQuestion[]
    editingQuestionId: string | null
    editingQuestionValue: string
}

export type PdfValidatorAction =
    | { type: 'PATCH'; payload: Partial<PdfValidatorState> }
    | { type: 'SET_STANDARD_CHECKS'; payload: SetStateAction<ValidationQuestion[]> }
    | { type: 'SET_QUESTIONS'; payload: SetStateAction<ValidationQuestion[]> }
    | { type: 'START_EDITING_QUESTION'; payload: { id: string; value: string } }
    | { type: 'UPDATE_EDITING_QUESTION_VALUE'; payload: string }
    | { type: 'CLEAR_EDITING_QUESTION_STATE' }
    | { type: 'COMMIT_EDITING_QUESTION' }
    | { type: 'ADD_EMPTY_QUESTION'; payload: { id: string } }
    | { type: 'DELETE_QUESTION'; payload: { id: string } }

const resolveSetStateAction = <T>(payload: SetStateAction<T>, previous: T): T =>
    typeof payload === 'function' ? (payload as (previousValue: T) => T)(previous) : payload

type PendingQuestionEdit = {
    editingQuestionId: string | null
    editingQuestionValue: string
}

export const applyPendingQuestionEdit = <T extends QuestionItem<unknown>>(baseQuestions: T[], pendingEdit: PendingQuestionEdit): T[] => {
    const { editingQuestionId, editingQuestionValue } = pendingEdit
    if (!editingQuestionId) {
        return baseQuestions
    }

    const activeQuestion = baseQuestions.find((question) => question.id === editingQuestionId)
    if (!activeQuestion) {
        return baseQuestions
    }

    const trimmedQuestion = editingQuestionValue.trim()
    if (trimmedQuestion.length === 0) {
        if (activeQuestion.question.trim().length === 0) {
            return baseQuestions.filter((question) => question.id !== editingQuestionId)
        }
        return baseQuestions
    }

    return baseQuestions.map((question) => {
        if (question.id !== editingQuestionId) {
            return question
        }

        const isChanged = question.question.trim() !== trimmedQuestion
        if (!isChanged) {
            return question
        }

        return {
            ...question,
            question: trimmedQuestion,
            result: null,
            isPending: false,
        }
    })
}

const emptyEditingState = {
    editingQuestionId: null,
    editingQuestionValue: '',
}

export const createInitialPdfValidatorState = (
    standardCheckDefinitions?: ReadonlyArray<ValidationQuestionDefinition>
): PdfValidatorState => ({
    uploadedFile: null,
    pdfUrl: null,
    pageTexts: [],
    uploadError: null,
    checksError: null,
    questionsError: null,
    tableOfContentsError: null,
    isPreprocessing: false,
    isTableOfContentsLoading: false,
    isChecksRunning: false,
    isQuestionsRunning: false,
    focusHighlight: null,
    hasLoadedTableOfContents: false,
    tableOfContentsEntries: [],
    standardChecks: createStandardChecks(standardCheckDefinitions),
    questions: [],
    editingQuestionId: null,
    editingQuestionValue: '',
})

export const pdfValidatorReducer = (state: PdfValidatorState, action: PdfValidatorAction): PdfValidatorState => {
    switch (action.type) {
        case 'PATCH':
            return { ...state, ...action.payload }
        case 'SET_STANDARD_CHECKS':
            return {
                ...state,
                standardChecks: resolveSetStateAction(action.payload, state.standardChecks),
            }
        case 'SET_QUESTIONS':
            return {
                ...state,
                questions: resolveSetStateAction(action.payload, state.questions),
            }
        case 'START_EDITING_QUESTION':
            return {
                ...state,
                editingQuestionId: action.payload.id,
                editingQuestionValue: action.payload.value,
            }
        case 'UPDATE_EDITING_QUESTION_VALUE':
            return {
                ...state,
                editingQuestionValue: action.payload,
            }
        case 'CLEAR_EDITING_QUESTION_STATE':
            return {
                ...state,
                ...emptyEditingState,
            }
        case 'COMMIT_EDITING_QUESTION':
            return {
                ...state,
                questions: applyPendingQuestionEdit(state.questions, {
                    editingQuestionId: state.editingQuestionId,
                    editingQuestionValue: state.editingQuestionValue,
                }),
                ...emptyEditingState,
            }
        case 'ADD_EMPTY_QUESTION': {
            const nextQuestions = applyPendingQuestionEdit(state.questions, {
                editingQuestionId: state.editingQuestionId,
                editingQuestionValue: state.editingQuestionValue,
            })
            return {
                ...state,
                questions: [{ id: action.payload.id, question: '', result: null, isPending: false }, ...nextQuestions],
                editingQuestionId: action.payload.id,
                editingQuestionValue: '',
            }
        }
        case 'DELETE_QUESTION': {
            const nextQuestions = state.questions.filter((question) => question.id !== action.payload.id)
            if (state.editingQuestionId === action.payload.id) {
                return {
                    ...state,
                    questions: nextQuestions,
                    ...emptyEditingState,
                }
            }
            return {
                ...state,
                questions: nextQuestions,
            }
        }
        default:
            return state
    }
}
